// 사용법 안내 투어 (버튼을 눌렀을 때만 시작) — 화면과 무관한 공용 엔진.
// 대상은 data-tour 속성으로 찾고, 단계 정의·문구는 각 화면의 tourContent 가 넘긴다.
// 화면 전체를 흐리게 덮고, 현재 단계의 요소만 구멍을 내 보여주면서 옆에 안내 메모를 띄운다.
// 외부 라이브러리 없이 data-tour 속성으로 대상 요소를 찾고, 화면에 없는 단계는 건너뛴다.
//
// 부드러운 전환을 위해:
// - 스크롤과 하이라이트를 하나의 트윈(450ms ease-in-out)으로 함께 움직인다. 브라우저 smooth scroll을
//   따로 쓰면 두 움직임이 겹쳐 목표가 흔들리고 링이 뒤따라가며 끊겨 보인다.
// - 매 프레임 React 상태를 갱신하지 않고 ref로 DOM 스타일만 직접 쓴다 (재렌더 비용 제거).
// - 이동 중에는 배경 흐림(backdrop-filter)을 끄고 어둡게만 덮은 뒤, 멈추면 흐림을 켠다.
//   전체 화면 흐림을 매 프레임 다시 계산하는 것이 프레임 드롭의 주원인이다.
//
// 끝맺음은 둘로 갈린다: onClose(닫기·Esc)와 onFinish(마지막 '완료'). 끝까지 본 사람만
// 다음 화면으로 넘기려면 후자를 쓴다 — 물린 사람까지 끌고 가면 안 된다.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './Tour.module.css';

/** 단계가 열어 둘 화면 이름. 화면마다 뜻이 다르므로 문자열로 둔다 (예: 'media' 팝업, 'edit' 수정 모드). */
export type TourScene = string;

export interface TourStep {
  /** 대상 요소의 data-tour 값 */
  target: string;
  title: string;
  body: string;
  /** 이 단계에서 열어 둘 화면(예: 매체 전달 팝업). 페이지가 onSceneChange로 열고 닫는다 */
  scene?: TourScene;
  /** 안내 카드 아래에 붙는 바깥 문서 링크. 투어가 못 담는 상세를 이어 준다 */
  link?: { href: string; label: string };
}

interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const PAD = 6; // 하이라이트 여백(px)
const TIP_W = 300; // 안내 카드 폭(px)
const TIP_H = 170; // 안내 카드 높이 추정(px) — 위/아래 배치 판단용
const GAP = 12; // 하이라이트와 카드 간격(px)
const RADIUS = 10; // 구멍 모서리 반경(px)
const DURATION = 450; // 전환 시간(ms)

const easeInOut = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

function findTarget(name: string): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-tour="${name}"]`);
}

/** 대상의 뷰포트 기준 사각형(여백 포함) */
function viewportRect(el: HTMLElement): Rect {
  const r = el.getBoundingClientRect();
  return { top: r.top - PAD, left: r.left - PAD, width: r.width + PAD * 2, height: r.height + PAD * 2 };
}

/** 대상을 실제로 스크롤하는 조상 — Orbit 레이아웃은 window가 아니라 본문 영역이 스크롤 컨테이너다. */
function scrollParentOf(el: HTMLElement): HTMLElement {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body) {
    const { overflowY } = getComputedStyle(node);
    if ((overflowY === 'auto' || overflowY === 'scroll') && node.scrollHeight > node.clientHeight + 1) return node;
    node = node.parentElement;
  }
  return (document.scrollingElement as HTMLElement | null) ?? document.documentElement;
}

/** 대상을 스크롤 컨테이너 세로 중앙에 놓는 scrollTop. */
function targetScrollTop(el: HTMLElement, container: HTMLElement): number {
  const r = el.getBoundingClientRect();
  const isRoot = container === document.scrollingElement || container === document.documentElement;
  const viewTop = isRoot ? 0 : container.getBoundingClientRect().top;
  const viewH = isRoot ? window.innerHeight : container.clientHeight;
  const desired = container.scrollTop + (r.top - viewTop) + r.height / 2 - viewH / 2;
  const max = Math.max(0, container.scrollHeight - viewH);
  return Math.min(max, Math.max(0, desired));
}

// 화면 전체 사각형에서 대상 영역(둥근 사각형)을 evenodd로 파낸 path.
// 배경 하나에 구멍을 내므로 4분할 패널 방식의 이음새(선)·깨짐이 생기지 않는다.
function holePath(vw: number, vh: number, r: Rect): string {
  const x = r.left;
  const y = r.top;
  const w = Math.max(0, r.width);
  const h = Math.max(0, r.height);
  const rad = Math.min(RADIUS, w / 2, h / 2);
  const hole =
    `M${x + rad} ${y}h${w - 2 * rad}a${rad} ${rad} 0 0 1 ${rad} ${rad}v${h - 2 * rad}` +
    `a${rad} ${rad} 0 0 1 ${-rad} ${rad}h${-(w - 2 * rad)}a${rad} ${rad} 0 0 1 ${-rad} ${-rad}` +
    `v${-(h - 2 * rad)}a${rad} ${rad} 0 0 1 ${rad} ${-rad}Z`;
  return `path(evenodd, "M0 0H${vw}V${vh}H0Z ${hole}")`;
}

function tipPosition(r: Rect): { top: number; left: number } {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const below = r.top + r.height + GAP + TIP_H < vh;
  const top = below ? r.top + r.height + GAP : Math.max(12, r.top - GAP - TIP_H);
  const left = Math.min(Math.max(12, r.left), vw - TIP_W - 12);
  return { top, left };
}

export function Tour({
  steps,
  onClose,
  onFinish,
  demo = false,
  onSceneChange,
}: {
  steps: TourStep[];
  /** 닫기·Esc — 투어를 물린다. 시작 전 상태로 되돌리는 자리다 */
  onClose: () => void;
  /** 마지막 단계에서 '완료'를 눌렀을 때. 없으면 onClose 와 같다.
   *  끝까지 본 사람만 다음 화면으로 넘기는 등, 물린 것과 다르게 다뤄야 할 때 쓴다 */
  onFinish?: () => void;
  demo?: boolean;
  onSceneChange?: (scene: TourScene | null) => void;
}) {
  // 화면에 실제로 존재하는 단계만 남긴다 (예: 이벤트 미선택 시 Reward 카드 단계는 건너뜀).
  // scene이 있는 단계는 그 화면을 연 뒤에 대상이 생기므로 여기서 걸러내지 않는다.
  const available = useMemo(() => steps.filter((s) => s.scene || findTarget(s.target)), [steps]);
  const [index, setIndex] = useState(0);
  const step = available[index];

  const shadeRef = useRef<HTMLDivElement | null>(null);
  const ringRef = useRef<HTMLDivElement | null>(null);
  const tipRef = useRef<HTMLDivElement | null>(null);
  const currentRef = useRef<Rect | null>(null); // 지금 그려진 사각형
  const animRef = useRef<number>(0);

  // DOM에 직접 그린다 — 매 프레임 React 재렌더 없이.
  const paint = useCallback((r: Rect) => {
    currentRef.current = r;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    if (shadeRef.current) shadeRef.current.style.clipPath = holePath(vw, vh, r);
    if (ringRef.current) {
      const s = ringRef.current.style;
      s.top = `${r.top}px`;
      s.left = `${r.left}px`;
      s.width = `${r.width}px`;
      s.height = `${r.height}px`;
    }
    if (tipRef.current) {
      const p = tipPosition(r);
      tipRef.current.style.top = `${p.top}px`;
      tipRef.current.style.left = `${p.left}px`;
    }
  }, []);

  const setMoving = useCallback((moving: boolean) => {
    shadeRef.current?.classList.toggle(styles.tourShadeMoving, moving);
  }, []);

  // 단계의 scene을 페이지에 알린다 (매체 전달 팝업 열기/닫기). 투어가 끝나면 null.
  useEffect(() => {
    onSceneChange?.(step?.scene ?? null);
  }, [step, onSceneChange]);
  useEffect(() => () => onSceneChange?.(null), [onSceneChange]);

  // 단계 전환: 스크롤 + 하이라이트를 하나의 트윈으로 함께 움직인다.
  // 대상이 아직 없으면 최대 0.5초 기다렸다가 건너뛴다. 주로 scene 단계가 여기 걸린다 —
  // scene 없는 단계는 위 available 필터에서 이미 빠졌기 때문이다(예: IAP 미선택).
  // 마지막 단계라 건너뛸 곳이 없으면 닫는다. 그냥 두면 0.5초 주기로 영원히 재시도하면서
  // 링이 앞 단계에 멈춘 채 남는다(조회 권한만 있는 사용자의 '변경 사항' 단계가 그랬다).
  // onFinish 가 아니라 onClose 다 — 보이지도 않은 단계를 '끝까지 봤다'로 칠 수 없다.
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    if (!step) return;
    const el = findTarget(step.target);
    if (!el) {
      // 타이머 기반(50ms × 10 = 0.5s): rAF는 백그라운드 탭에서 억제돼 대기가 수십 초로 늘어난다.
      if (retry < 10) {
        const timer = window.setTimeout(() => setRetry((r) => r + 1), 50);
        return () => window.clearTimeout(timer);
      }
      setRetry(0);
      if (index >= available.length - 1) onClose();
      else setIndex((i) => i + 1);
      return;
    }
    if (retry !== 0) setRetry(0);
    cancelAnimationFrame(animRef.current);

    const container = scrollParentOf(el);
    const startScroll = container.scrollTop;
    const endScroll = targetScrollTop(el, container);
    // 목적지 스크롤 기준의 대상 사각형 (현재 뷰포트 사각형에서 스크롤 차이만큼 보정)
    const nowRect = viewportRect(el);
    const endRect: Rect = { ...nowRect, top: nowRect.top - (endScroll - startScroll) };
    const from = currentRef.current;

    if (!from) {
      container.scrollTop = endScroll;
      paint(viewportRect(el));
      return;
    }

    setMoving(true);
    const t0 = performance.now();
    // 백그라운드 탭 등 rAF가 억제되면 프레임이 안 와 링이 멈춘 듯 보인다 → 시간이 지나면 타이머로 마무리.
    const finish = () => {
      cancelAnimationFrame(animRef.current);
      container.scrollTop = endScroll;
      paint(viewportRect(el));
      setMoving(false);
    };
    const fallback = window.setTimeout(finish, DURATION + 80);
    const tick = (now: number) => {
      const t = Math.min(1, (now - t0) / DURATION);
      const e = easeInOut(t);
      container.scrollTop = startScroll + (endScroll - startScroll) * e;
      paint({
        top: from.top + (endRect.top - from.top) * e,
        left: from.left + (endRect.left - from.left) * e,
        width: from.width + (endRect.width - from.width) * e,
        height: from.height + (endRect.height - from.height) * e,
      });
      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        // 레이아웃 오차 보정: 실제 위치로 스냅한 뒤 흐림을 켠다.
        window.clearTimeout(fallback);
        paint(viewportRect(el));
        setMoving(false);
      }
    };
    animRef.current = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.clearTimeout(fallback);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, retry, paint, setMoving]);

  // 사용자가 직접 스크롤/리사이즈하면 즉시 따라간다 (전환 애니메이션 중에는 트윈이 우선).
  useEffect(() => {
    const follow = () => {
      if (!step) return;
      const el = findTarget(step.target);
      if (el && shadeRef.current && !shadeRef.current.classList.contains(styles.tourShadeMoving)) paint(viewportRect(el));
    };
    window.addEventListener('resize', follow);
    window.addEventListener('scroll', follow, true);
    return () => {
      window.removeEventListener('resize', follow);
      window.removeEventListener('scroll', follow, true);
    };
  }, [step, paint]);

  const next = useCallback(() => {
    if (index >= available.length - 1) (onFinish ?? onClose)();
    else setIndex((i) => i + 1);
  }, [index, available.length, onClose, onFinish]);
  const prev = useCallback(() => setIndex((i) => Math.max(0, i - 1)), []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowRight' || e.key === 'Enter') next();
      else if (e.key === 'ArrowLeft') prev();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [next, prev, onClose]);

  if (!step) return null;

  return createPortal(
    <div className={styles.tourRoot} role="dialog" aria-label="사용법 안내">
      {/* 흐림 배경 한 장 — 대상 영역만 evenodd path로 파낸다 (paint가 clip-path를 직접 갱신) */}
      <div ref={shadeRef} className={styles.tourShade} />
      <div ref={ringRef} className={styles.tourRing} />

      <div ref={tipRef} className={styles.tourTip} style={{ width: TIP_W }}>
        <div className={styles.tourTipHead}>
          <span className={styles.tourStepNo}>
            {index + 1} / {available.length}
            {demo && <span className={styles.tourDemoBadge}>예시 데이터</span>}
          </span>
          <button type="button" className={styles.tourClose} onClick={onClose} aria-label="안내 닫기">
            ×
          </button>
        </div>
        <div key={step.target} className={styles.tourTipContent}>
          <div className={styles.tourTitle}>{step.title}</div>
          <p className={styles.tourBody}>{step.body}</p>
          {step.link && (
            <a
              className={styles.tourLink}
              href={step.link.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              {step.link.label}
            </a>
          )}
        </div>
        <div className={styles.tourNav}>
          <button type="button" className={styles.tourBtnGhost} onClick={prev} disabled={index === 0}>
            이전
          </button>
          <button type="button" className={styles.tourBtnPrimary} onClick={next}>
            {index >= available.length - 1 ? '완료' : '다음'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
